/**
 * Shared utilities for API modules.
 *
 * buildQS     — converts a params object to a URL query string, skipping null/undefined/empty.
 * createCrudApi — factory for standard resource APIs (list, get, create, update, delete,
 *                 duplicate, updateStatus, createBatch). Used for feeSchedule, labTest,
 *                 procedure, and medicineItem which all share the same shape.
 */
import { fetchApi } from './client';

// ─── buildQS ─────────────────────────────────────────────────────────────────

/**
 * Converts a plain object of query params into a URL query string (with leading `?`).
 * Skips keys whose value is null, undefined, or empty string.
 * Arrays are appended as repeated params: `statuses=A&statuses=B`.
 *
 * @example
 * buildQS({ page: 1, search: 'foo', filter: undefined })
 * // → '?page=1&search=foo'
 */
export function buildQS(params?: Record<string, any>): string {
  if (!params) return '';
  const p = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val == null || val === '') continue;
    if (Array.isArray(val)) {
      val.forEach(v => v != null && v !== '' && p.append(key, String(v)));
    } else {
      p.set(key, String(val));
    }
  }
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

// ─── createCrudApi ────────────────────────────────────────────────────────────

export interface CrudFilters {
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
  [key: string]: any;
}

/**
 * Factory for standard CRUD resources that share the same endpoint shape.
 *
 * All four admin fee-config resources (feeSchedule, labTest, procedure, medicineItem)
 * have identical CRUD operations. This factory eliminates the duplication.
 *
 * @param basePath   — e.g. '/api/fee-schedules'
 * @param cleanFilters — optional transform applied before building the query string;
 *                       use to strip UI sentinel values like 'All Departments'
 */
export function createCrudApi(
  basePath: string,
  cleanFilters?: (filters: CrudFilters) => Record<string, any>
) {
  return {
    list: (filters?: CrudFilters) => {
      const cleaned = cleanFilters && filters ? cleanFilters(filters) : (filters ?? {});
      return fetchApi<any>(`${basePath}${buildQS(cleaned)}`);
    },

    get: (id: string) =>
      fetchApi<any>(`${basePath}/${id}`),

    create: (data: any) =>
      fetchApi<any>(basePath, { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: any) =>
      fetchApi<any>(`${basePath}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    delete: (id: string) =>
      fetchApi<any>(`${basePath}/${id}`, { method: 'DELETE' }),

    duplicate: (id: string) =>
      fetchApi<any>(`${basePath}/${id}/duplicate`, { method: 'POST' }),

    updateStatus: (id: string, status: string) =>
      fetchApi<any>(`${basePath}/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

    createBatch: (items: any[]) =>
      fetchApi<any>(`${basePath}/batch`, { method: 'POST', body: JSON.stringify(items) }),
  };
}
